// Tax-inclusive (VAT-inclusive) pricing helper.
//
// Some businesses (PH, UK/EU, AU/NZ) set item prices that ALREADY contain the
// tax. In that mode the total the customer pays does NOT change - we only need
// to EXTRACT and show how much of that total was tax, for receipt compliance.
//
// Compliant extraction (what BIR / HMRC / ATO require):
//   tax = gross * r / (1 + r)      net = gross - tax
// NOT the naive `gross * r` (that would tax the net at the wrong effective rate).
//   e.g. 100 incl. 12%  ->  tax 10.71, net 89.29  (not 12 / 88)
//
// This is opt-in and separate from Auto Adjustments (which ADD tax on top).

export const extractInclusiveTax = function (gross, ratePercent) {
  const g = Number(gross);
  const rate = Number(ratePercent);
  if (!g || g <= 0 || !rate || rate <= 0) return null;
  const r = rate / 100;
  const tax = (g * r) / (1 + r);
  return { net: g - tax, tax, rate };
};

// The country's usual term for the tax (mirrors orderTypeLabel / invoiceLabel).
// Country from an explicit arg, else the stored business country.
export const defaultTaxLabel = function (country) {
  const c = (
    country ||
    localStorage.getItem('pointbunny_business_country') ||
    ''
  ).toUpperCase();
  if (c === 'US') return 'Sales Tax';
  if (['AU', 'NZ'].includes(c)) return 'GST';
  const VAT = [
    'PH', 'GB', 'UK', 'IE', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'PT', 'AT',
    'FI', 'SE', 'DK', 'PL', 'GR', 'CZ', 'RO', 'HU', 'SK', 'BG', 'HR', 'LT',
    'LV', 'EE', 'SI', 'LU', 'CY', 'MT',
  ];
  if (VAT.includes(c)) return 'VAT';
  return 'Tax';
};

// The canonical tax-name vocabulary (2026-07-17: the name is a dropdown pick,
// NOT free text - an arbitrary string on a legal receipt line was both an XSS
// magnet and a compliance risk). Covers the target markets first (US = Sales
// Tax, UK/EU/PH = VAT, AU/NZ = GST) plus the common international terms.
export const TAX_LABELS = [
  'VAT',              // UK, EU, PH, and most of the world
  'GST',              // AU, NZ, CA (federal), SG, IN
  'Sales Tax',        // US
  'Tax',              // generic
  'HST',              // CA harmonized provinces
  'PST',              // CA provincial
  'IVA',              // ES, IT, PT, LatAm
  'TVA',              // FR
  'MwSt',             // DE, AT
  'SST',              // MY
  'Consumption Tax',  // JP
];

// The label actually shown on receipts etc.: the owner's picked name if one is
// stored (cached per-device from businesses.settings.taxInclusiveLabel), else
// the country default. Only values from TAX_LABELS are honored - a legacy
// free-text value (the field used to be a text input) falls back to the
// country default rather than printing arbitrary text on receipts.
export const taxLabel = function (country) {
  const override = (localStorage.getItem('pointbunny_tax_label') || '').trim();
  return TAX_LABELS.includes(override) ? override : defaultTaxLabel(country);
};

// Per-item VAT/tax exemption (2026-07-07). Splits a sale's/cart's item gross
// into taxable vs exempt buckets so extractInclusiveTax runs only over the
// taxable portion. isExempt is a predicate so a caller can check a stamped
// historical flag (sale.items, already-recorded sales) or resolve live from
// current menu state (the checkout preview, before a sale is recorded).
export const splitTaxableGross = function (items, isExempt) {
  let taxableGross = 0;
  let exemptGross = 0;
  (items ?? []).forEach((it) => {
    const g = Number(it.totalPrice) || 0;
    if (isExempt(it)) exemptGross += g; else taxableGross += g;
  });
  return { taxableGross, exemptGross };
};

// Canonical exempt-line vocabulary (dropdown, same logic as TAX_LABELS).
export const EXEMPT_LABELS = [
  'Tax-Exempt',   // US and generic
  'Zero-Rated',   // UK/EU/PH VAT term
  'GST-Free',     // AU/NZ
  'Exempt',       // short generic
  'Non-Taxable',  // US alternative
];

// The label actually shown for the exempt-sales line: the owner's picked name
// if stored (businesses.settings.taxExemptLabel, cached per-device), else the
// country default. Same canonical-only rule as taxLabel.
export const exemptLabel = function (country) {
  const override = (localStorage.getItem('pointbunny_tax_exempt_label') || '').trim();
  return EXEMPT_LABELS.includes(override) ? override : defaultExemptLabel(country);
};

// The country's usual term for the exempt-sales receipt line. Mirrors
// defaultTaxLabel's exact country grouping so it reads naturally per market:
// UK/EU-style VAT countries call it Zero-Rated (the common real-world term for
// most exempted goods there), AU/NZ GST-Free, everyone else (incl. the US, the
// primary market) Tax-Exempt.
export const defaultExemptLabel = function (country) {
  const c = (
    country ||
    localStorage.getItem('pointbunny_business_country') ||
    ''
  ).toUpperCase();
  if (['AU', 'NZ'].includes(c)) return 'GST-Free';
  const VAT = [
    'PH', 'GB', 'UK', 'IE', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'PT', 'AT',
    'FI', 'SE', 'DK', 'PL', 'GR', 'CZ', 'RO', 'HU', 'SK', 'BG', 'HR', 'LT',
    'LV', 'EE', 'SI', 'LU', 'CY', 'MT',
  ];
  if (VAT.includes(c)) return 'Zero-Rated';
  return 'Tax-Exempt';
};
